import { AccountInfo, BrowserAuthError, InteractionRequiredAuthError } from '@azure/msal-browser';
import { AuthenticationResult } from '@azure/msal-browser';

import { getConfig } from '../config/globalConfig';
import { getMsalInstance, loginRequest, protectedResources } from '../components/authentication/msalConfig';
import { toNavigationRole } from '../models/user/userRole';

import { userManagementService } from './user-management.service';


export const authService = {
  /* ===========================
     LOGIN / LOGOUT
  =========================== */

  loginPopup: async (): Promise<AuthenticationResult> => {
    const msal = getMsalInstance();

    const response = await msal.loginPopup({
      scopes: loginRequest.scopes,
    });

    if (response.account) {
      msal.setActiveAccount(response.account);
    }

    return response;
  },

  loginRedirect: async (): Promise<void> => {
    const msal = getMsalInstance();
    await msal.loginRedirect({
      scopes: loginRequest.scopes,
    });
  },

  logout: async (): Promise<void> => {
    const msal = getMsalInstance();

    userManagementService.removeStoredOrganisatie();

    await msal.logoutRedirect({
      postLogoutRedirectUri: "/",
    });
  },

  /* ===========================
     ACCOUNT INFO
  =========================== */

  isLoggedIn: (): boolean => {
    const msal = getMsalInstance();
    return msal.getAllAccounts().length > 0;
  },

  getAccount: (): AccountInfo | null => {
    const msal = getMsalInstance();

    return (
      msal.getActiveAccount() ??
      msal.getAllAccounts()[0] ??
      null
    );
  },

  isAdmin: (): boolean => {
    const account = authService.getAccount();
    if (!account) return false;

    const roles = account.idTokenClaims?.roles as string[] | undefined;
    return roles?.includes("admin") ?? false;
  },

  getRole: (): string => {
    if (authService.isAdmin()) return "Admin";

    const storedOrganisatie = userManagementService.getStoredOrganisatie();

    if (storedOrganisatie) {
      return toNavigationRole(storedOrganisatie.userType);
    }

    return "";
  },

  isReadOnly: (): boolean => {
    return authService.getRole() === "ReadOnly";
  },

  canCrud: (): boolean => {
    const role = authService.getRole();
    return role === "Admin" || role === "KeyUser" || role === "User";
  },

  isKeyUser: (): boolean => {
    const role = authService.getRole();
    return role === "KeyUser" || role === "Admin";
  },

  getInitials: (): string => {
    const account = authService.getAccount();
    const name = account?.name || "";

    const parts = name.split(" ");
    const first = parts[0]?.charAt(0) ?? "";
    const last = parts[1]?.charAt(0) ?? "";

    return `${first}${last}`.toUpperCase();
  },

  /* ===========================
     TOKEN HANDLING
  =========================== */

  getAccessToken: async (): Promise<string> => {
    const msal = getMsalInstance();
    const appConfig = getConfig();

    const account =
      msal.getActiveAccount() ??
      msal.getAllAccounts()[0];

    if (!account) {
      throw new Error("No account available");
    }

    try {
      const scopes =
        protectedResources.get(appConfig.portalUrl) ??
        loginRequest.scopes;

      const response = await msal.acquireTokenSilent({
        account,
        scopes,
        redirectUri: `${window.location.origin}/auth/silent-redirect.html`,
      });

      return response.accessToken;
    }
    catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // fallback naar redirect (silent refresh niet mogelijk)
        await msal.acquireTokenRedirect({
          scopes: loginRequest.scopes,
        });
      }

      // iframe timeout bij silent token renewal — fallback naar interactieve flow
      if (error instanceof BrowserAuthError && (error as any).errorCode === 'monitor_window_timeout') {
        await msal.acquireTokenRedirect({
          scopes: loginRequest.scopes,
        });
      }

      throw error;
    }
  },
};

